/**
 * Session state built on TanStack Query (frozen auth contract, spec §15).
 *
 * - `useMe()` is the single source of truth for the current session; it also
 *   feeds the CSRF token cache in lib/api.
 * - `usePermissions()` derives deny-by-default permission checks; the
 *   Administrator role bypasses all checks.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ALL_PERMISSIONS,
  type Locale,
  type Permission,
} from "@offertli/shared";
import { apiGet, apiPatch, apiPost, setCsrfToken, ApiError } from "./api.js";

export interface SessionRole {
  id: string;
  name: string;
  isAdmin: boolean;
}

export interface SessionUser {
  id: string;
  email: string | null;
  loginName: string | null;
  isSystemAdmin: boolean;
  firstName: string;
  lastName: string;
  locale: string;
  status: string;
  roles: SessionRole[];
  permissions: string[];
}

export interface MeResponse {
  user: SessionUser;
  csrfToken: string;
  setup: { completed: boolean };
  impersonating: { actorLoginName: string | null; actorName: string } | null;
}

export const ME_QUERY_KEY = ["me"] as const;

async function fetchMe(): Promise<MeResponse> {
  const me = await apiGet<MeResponse>("/auth/me");
  setCsrfToken(me.csrfToken);
  return me;
}

export function useMe() {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** True while the session query is in its first load. */
export function usePermissions(): {
  has: (key: Permission) => boolean;
  hasAny: (keys: Permission[]) => boolean;
  isAdmin: boolean;
} {
  const { data } = useMe();
  const user = data?.user;
  const isAdmin =
    user?.isSystemAdmin || user?.roles.some((role) => role.isAdmin) || false;
  const granted = new Set(
    isAdmin ? ALL_PERMISSIONS : (user?.permissions ?? []),
  );
  return {
    isAdmin,
    has: (key) => granted.has(key),
    hasAny: (keys) => keys.some((key) => granted.has(key)),
  };
}

export interface LoginInput {
  identifier: string;
  password: string;
  rememberMe?: boolean;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) =>
      apiPost<{ user: SessionUser }>("/auth/login", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useUpdateLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) =>
      apiPatch<{ user: SessionUser }>("/auth/me/locale", { locale }),
    onSuccess: (response) => {
      queryClient.setQueryData<MeResponse>(ME_QUERY_KEY, (current) =>
        current ? { ...current, user: response.user } : current,
      );
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<void>("/auth/logout"),
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
    },
  });
}

/** Resolve the authenticated user after login / setup / invitation accept. */
export function useInvalidateSession() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}
